import type { InvoiceDetailResponse } from '../../../types/invoice';

export interface PartIndexRemapLayout {
    lineItemBackendIndexByCurrentIndex: (number | undefined)[];
    currentLineItems: InvoiceDetailResponse['lineItems'];
    currentFreightItems: InvoiceDetailResponse['freightItems'];
    currentFuelItems: InvoiceDetailResponse['fuelItems'];
    currentRestockingItems: InvoiceDetailResponse['restockingFeeItems'];
    currentDiscountItems: InvoiceDetailResponse['discountItems'];
    currentIgnoredItems: InvoiceDetailResponse['ignoredParts'];
    oldLineCount: number;
    oldFreightCount: number;
    oldFuelCount: number;
    oldRestockingCount: number;
    oldDiscountCount: number;
    oldIgnoredCount: number;
}

export function buildPartIndexRemapLayout(
    invoice: InvoiceDetailResponse | undefined,
    editedFields: Partial<InvoiceDetailResponse>,
    lineItemBackendIndexByCurrentIndex: (number | undefined)[],
): PartIndexRemapLayout {
    const currentLineItems = editedFields.lineItems ?? invoice?.lineItems ?? [];
    const currentFreightItems = editedFields.freightItems ?? invoice?.freightItems ?? [];
    const currentFuelItems = editedFields.fuelItems ?? invoice?.fuelItems ?? [];
    const currentRestockingItems = editedFields.restockingFeeItems ?? invoice?.restockingFeeItems ?? [];
    const currentDiscountItems = editedFields.discountItems ?? invoice?.discountItems ?? [];
    const currentIgnoredItems = editedFields.ignoredParts ?? invoice?.ignoredParts ?? [];

    return {
        lineItemBackendIndexByCurrentIndex,
        currentLineItems,
        currentFreightItems,
        currentFuelItems,
        currentRestockingItems,
        currentDiscountItems,
        currentIgnoredItems,
        oldLineCount: invoice?.lineItems?.length ?? 0,
        oldFreightCount: invoice?.freightItems?.length ?? 0,
        oldFuelCount: invoice?.fuelItems?.length ?? 0,
        oldRestockingCount: invoice?.restockingFeeItems?.length ?? 0,
        oldDiscountCount: invoice?.discountItems?.length ?? 0,
        oldIgnoredCount: invoice?.ignoredParts?.length ?? 0,
    };
}

/** Map global part index from last-saved layout to current edited layout. */
export function remapStoredPartIndex(layout: PartIndexRemapLayout, oldIndex: number): number | undefined {
    if (oldIndex < 0) return oldIndex;

    const newLineCount = layout.currentLineItems.length;
    const newFreightCount = layout.currentFreightItems?.length ?? 0;
    const newFuelCount = layout.currentFuelItems?.length ?? 0;
    const newRestockingCount = layout.currentRestockingItems?.length ?? 0;
    const newDiscountCount = layout.currentDiscountItems?.length ?? 0;
    const newIgnoredCount = layout.currentIgnoredItems?.length ?? 0;

    if (oldIndex < layout.oldLineCount) {
        const mapped = layout.lineItemBackendIndexByCurrentIndex.findIndex((backendIdx) => backendIdx === oldIndex);
        return mapped >= 0 ? mapped : undefined;
    }

    let offset = layout.oldLineCount;
    if (oldIndex < offset + layout.oldFreightCount) {
        const local = oldIndex - offset;
        return local < newFreightCount ? newLineCount + local : undefined;
    }
    offset += layout.oldFreightCount;

    if (oldIndex < offset + layout.oldFuelCount) {
        const local = oldIndex - offset;
        return local < newFuelCount ? newLineCount + newFreightCount + local : undefined;
    }
    offset += layout.oldFuelCount;

    if (oldIndex < offset + layout.oldRestockingCount) {
        const local = oldIndex - offset;
        return local < newRestockingCount ? newLineCount + newFreightCount + newFuelCount + local : undefined;
    }
    offset += layout.oldRestockingCount;

    if (oldIndex < offset + layout.oldDiscountCount) {
        const local = oldIndex - offset;
        return local < newDiscountCount
            ? newLineCount + newFreightCount + newFuelCount + newRestockingCount + local
            : undefined;
    }
    offset += layout.oldDiscountCount;

    if (oldIndex < offset + layout.oldIgnoredCount) {
        const local = oldIndex - offset;
        return local < newIgnoredCount
            ? newLineCount + newFreightCount + newFuelCount + newRestockingCount + newDiscountCount + local
            : undefined;
    }

    return undefined;
}
